import { Controller } from '@hotwired/stimulus';
import { isModalRequested } from '../utils/modal';

const LOADING_SVG = `
  <svg fill="#FFFFFF" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,19a8,8,0,1,1,8-8A8,8,0,0,1,12,20Z" opacity=".25" />
    <path d="M10.14,1.16a11,11,0,0,0-9,8.92A1.59,1.59,0,0,0,2.46,12,1.52,1.52,0,0,0,4.11,10.7a8,8,0,0,1,6.66-6.61A1.42,1.42,0,0,0,12,2.69h0A1.57,1.57,0,0,0,10.14,1.16Z">
      <animateTransform attributeName="transform" type="rotate" dur="0.75s" values="0 12 12;360 12 12" repeatCount="indefinite" />
    </path>
  </svg>
`;

/**
 * The remote modal controller can be attached to a button or link to open a
 * modal and load the given address via a <turbo-frame>.
 *
 * The modal will be opened instantly for fast feedback to the user, and show a
 * loading spinner to indicate an ongoing activity to the user. The spinner is
 * showed with a delay, so that actual quick remote fetches do not show a
 * spinner at all, not even briefly, to improve the perceived performance. The
 * loading spinner will only be shown when it takes a bit more time, so that the
 * user then knows that the action has been registered but is still ongoing.
 *
 * Examples:
 *
 *     <button data-controller="remote-modal" data-remote-modal-url="https://example.com/path/to/endpoint" />
 *
 * Using links are discouraged unless a navigational fallback to a regular page
 * visit is working and intended:
 *
 *     <a href="https://example.com/path/to/endpoint" data-controller="remote-modal">...</a>
 *
 * The URL can be path too, and will be resolved relative to the current window.
 *
 * A custom turbo frame ID can be passed in case multiple frames are expected or
 * the endpoint needs a specific Turbo-Frame ID:
 *
 *     <button
 *        data-controller="remote-modal"
 *        data-remote-modal-url="..."
 *        data-remote-modal-id="my-frame-id"
 *      />
 *
 * See `StimulusHelper#stimulus_remote_modal` on how to generate data attributes
 * on the server side:
 *
 *     = render Shared::Button::Component.new( \
 *         ...
 *         html_options: { **helpers.stimulus_remote_modal(my_path) },
 *       )
 */
export default class extends Controller {
  connect() {
    if (isModalRequested(this.#fetchURL())) {
      this.requestModal();
    }
  }

  requestModal(event) {
    if (event && this.#isNestedInteractiveElement(event.target)) return;

    let url = this.#fetchURL();
    if (!url) return;

    if (event && (event.metaKey || event.ctrlKey)) {
      if (this.element instanceof HTMLAnchorElement) return;

      event.preventDefault();
      window.open(new URL(url, window.location.href), '_blank', 'noopener');
      return;
    }

    event?.preventDefault();

    const src = new URL(url, window.location.href);
    const loadingText =
      this.element.dataset.remoteModalLoadingText || 'Loading...';
    // Keep the default in sync with
    // `Shared::ModalFrame::Component::REMOTE_FRAME_ID`, which server-side
    // responses and turbo-stream targets rely on.
    const frameId = this.element.dataset.remoteModalId || 'remote-modal';

    // Keep the dialog classes in sync with Shared::Modal::Component.
    /** @type {HTMLDialogElement} */
    const dialog = (
      <dialog
        aria-labelledby={`${frameId}-heading`}
        class="m-0 h-dvh max-h-dvh w-screen max-w-none overflow-y-auto border-0 bg-transparent p-0 backdrop:bg-black/50"
        data-controller="modal"
        data-turbo-temporary
      >
        {/* The frame classes only style the loading state: Turbo drops them
            when rendering the response, which centers itself instead. */}
        <turbo-frame
          class="group/modal-turbo-frame flex min-h-full w-full items-center justify-center"
          id={frameId}
          src={src}
        >
          <div class="flex justify-center">
            <div
              role="status"
              class="opacity-0 group-aria-[busy]/modal-turbo-frame:opacity-100 transition-opacity delay-500 duration-1000 ease-in-out"
            >
              <div class="size-20" innerHTML={LOADING_SVG} />
              {/* Labels the dialog until Turbo renders the response, which
                  replaces the frame content and brings the panel heading
                  carrying the same ID. */}
              <p class="sr-only text-center mt-4" id={`${frameId}-heading`}>
                {loadingText}
              </p>
            </div>
          </div>
        </turbo-frame>
      </dialog>
    );

    this.dialog = dialog;
    document.body.append(dialog);

    dialog.addEventListener('close', () => dialog.remove());
    dialog.showModal();
  }

  close() {
    this.dialog?.close();
  }

  #isNestedInteractiveElement(target) {
    const interactiveElement = target.closest?.('a, button');
    return interactiveElement && interactiveElement !== this.element;
  }

  #fetchURL() {
    if (this.element instanceof HTMLAnchorElement) {
      if (this.element.dataset.remoteModalUrl) {
        return new URL(
          this.element.dataset.remoteModalUrl,
          window.location.href,
        );
      } else if (this.element.href) {
        return new URL(this.element.href, window.location.href);
      }
    } else {
      if (this.element.dataset.remoteModalUrl) {
        return new URL(
          this.element.dataset.remoteModalUrl,
          window.location.href,
        );
      }
    }
  }
}
